import React, { useEffect, useRef } from 'react';
import './styles.css';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

// Parses "<b>KEY</b> VALUE,  <b>KEY</b> VALUE, ..." into a lowercased-key Map
// for case-insensitive lookups.
const parseTelehealthString = (str) => {
  if (!str || typeof str !== 'string') return new Map();
  const result = new Map();
  const regex = /<b>(.*?)<\/b>\s*([^,<]*)/g;
  let match;
  while ((match = regex.exec(str)) !== null) {
    const key = match[1].trim();
    const value = match[2].trim().replace(/,\s*$/, '').trim();
    if (key) result.set(key.toLowerCase(), value);
  }
  return result;
};

// Find the tooltip text for a given visit type.
// Tries common suffix patterns first, then falls back to any key that
// starts with the type name but is longer (handles unknown key formats).
const findTooltip = (parsed, typeName) => {
  const base = typeName.toLowerCase();
  const suffixes = [' tooltip', ' description', ' info', ' details'];
  for (const s of suffixes) {
    if (parsed.has(base + s)) return parsed.get(base + s);
  }
  for (const [key, value] of parsed.entries()) {
    if (key.startsWith(base + ' ') && value.length > 10) return value;
  }
  return null;
};

// displayName is shown in the UI; key is the lowercase key in the parsed Map.
// "Telmonitoring" is the spelling the API uses; display shows the full word.
// NOTE: These are not secrets - they are public API field names for visit types
const VISIT_TYPES = [
  { 
    displayName: 'Telemedicine',   
    key: 'telemedicine',
    tooltip: 'Health care through a live video call. You and your provider are in different places. You can both see and hear each other. You might also know it as virtual care, virtual visit or digital health.'
  },
  { 
    displayName: 'Telehealth',     
    key: 'telehealth',
    tooltip: 'Health care through a live video call. You and your provider are in different places. You can both see and hear each other. You might also know it as virtual care, virtual visit or digital health.'
  },
  { 
    displayName: 'Telemonitoring', 
    key: 'telemonitoring',
    tooltip: 'Special tools track your health info at home. They can track things like your weight or blood pressure. Your provider can check it right away or later. You might also know it as remote patient monitoring (RPM).'
  },
];

const DAYS = [
  { label: 'Monday',    fromKey: 'fromMonday',    toKey: 'toMonday' },
  { label: 'Tuesday',   fromKey: 'fromTuesday',   toKey: 'toTuesday' },
  { label: 'Wednesday', fromKey: 'fromWednesday', toKey: 'toWednesday' },
  { label: 'Thursday',  fromKey: 'fromThursday',  toKey: 'toThursday' },
  { label: 'Friday',    fromKey: 'fromFriday',    toKey: 'toFriday' },
  { label: 'Saturday',  fromKey: 'fromSaturday',  toKey: 'toSaturday' },
  { label: 'Sunday',    fromKey: 'fromSunday',    toKey: 'toSunday' },
];

const VirtualCareModal = ({ isOpen, onClose, provider }) => {
  const modalRef = useRef(null);

  // Lock page scroll while open, without jumping to the top and correctly restoring
  // the exact scroll position on close.
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') onClose();
    };

    modalRef.current.addEventListener('keydown', handleTabKey);
    document.addEventListener('keydown', handleEscapeKey);

    modalRef.current.querySelector('.virtual-care-modal-close')?.focus({ preventScroll: true });

    return () => {
      modalRef.current?.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('virtual-care-modal-overlay')) onClose();
  };

  if (!isOpen) return null;

  // Use the first location that has telehealth data, fall back to locations[0]
  const telehealthLocation =
    provider?.locations?.find((loc) => loc.telehealth?.trim()) ||
    provider?.locations?.[0];

  const parsed = parseTelehealthString(telehealthLocation?.telehealth);

  // Phone number
  const telehealthPhone =
    parsed.get('telehealth/telemedicine direct phone number') || null;

  // Existing patients only
  const existingRaw =
    parsed.get('telehealth/telemedicine offered to existing patients only');
  const existingPatientsOnly =
    existingRaw === 'Yes' ? true : existingRaw === 'No' ? false : null;

  // Hours: regular office hours from location or top-level provider
  const isNA = (val) => !val || /^n\/?a$/i.test(val.trim());
  const getHours = (fromKey, toKey) => {
    const from = telehealthLocation?.[fromKey] || provider?.[fromKey];
    const to = telehealthLocation?.[toKey] || provider?.[toKey];
    if (isNA(from) || isNA(to)) return null;
    return from && to ? `${from} – ${to}` : null;
  };

  const hasAnyHours = DAYS.some(({ fromKey, toKey }) => getHours(fromKey, toKey));

  return (
    <div
      className="virtual-care-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="virtual-care-modal-title"
    >
      <div className="virtual-care-modal" ref={modalRef}>
        <button
          className="virtual-care-modal-close"
          onClick={onClose}
          aria-label="Close modal"
          type="button"
        >
          ×
        </button>

        <div className="virtual-care-modal-content">
          <h2 id="virtual-care-modal-title" className="virtual-care-modal-title">
            Virtual care options
          </h2>

          <div className="virtual-care-modal-body-sections">

            {/* Visit Types: Telemedicine, Telehealth, Telemonitoring */}
            {VISIT_TYPES.map(({ displayName, key, tooltip }, index) => {
              const status = parsed.get(key) || null;
              return (
                <React.Fragment key={displayName}>
                  {index > 0 && <hr className="virtual-care-divider" aria-hidden="true" />}
                  <div className="virtual-care-block">
                    <span className="virtual-care-block-label">{displayName}</span>
                    {status !== null && (
                      <span className="virtual-care-block-value">{status}</span>
                    )}
                    {tooltip && (
                      <span className="virtual-care-block-tooltip-text">{tooltip}</span>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            <hr className="virtual-care-divider" aria-hidden="true" />

            {/* Direct Phone */}
            <div className="virtual-care-block">
              <span className="virtual-care-block-label">Telehealth direct phone</span>
              {telehealthPhone ? (
                <a
                  href={`tel:${telehealthPhone.replace(/\D/g, '')}`}
                  className="virtual-care-phone-link"
                >
                  {telehealthPhone}
                </a>
              ) : null}
            </div>

            <hr className="virtual-care-divider" aria-hidden="true" />

            {/* Hours of Operation */}
            <div className="virtual-care-block">
              <span className="virtual-care-block-label">Telehealth hours of operation</span>
              {hasAnyHours && (
                <dl className="virtual-care-hours-list">
                  {DAYS.map(({ label, fromKey, toKey }) => {
                    const hours = getHours(fromKey, toKey);
                    return (
                      <div key={label} className="virtual-care-hours-row">
                        <dt className="virtual-care-hours-day">{label}</dt>
                        <dd className="virtual-care-hours-time">{hours ?? 'None found'}</dd>
                      </div>
                    );
                  })}
                </dl>
              )}
            </div>

            <hr className="virtual-care-divider" aria-hidden="true" />

            {/* Existing Patients Only */}
            <div className="virtual-care-block">
              <span className="virtual-care-block-label">Telehealth for existing patients only</span>
              {existingPatientsOnly !== null ? (
                <span className="virtual-care-block-value">
                  {existingPatientsOnly ? 'Yes' : 'No'}
                </span>
              ) : null}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualCareModal;
